import { useState, useEffect, useRef } from "react";
import { uploadEmployeeFile } from "@/lib/fileUpload";
import { supabase } from "@/lib/supabase";
import type { Employee } from "@/types/employee";
import { useUser } from "@/context/UserContext";

interface BankFormProps {
  employee: Employee | null;
  onUpdate: (updated: Employee) => void;
  onToast: (msg: string, type?: "success" | "error") => void;
}

export default function BankForm({ employee, onUpdate, onToast }: BankFormProps) {
  const [form, setForm] = useState({
    bank_name: "",
    bank_branch: "",
    bank_code: "",
    bank_account_name: "",
    bank_account_number: "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { refreshProfileComplete } = useUser();

  const { id: employeeId, bank_name, bank_branch, bank_code,
    bank_account_name, bank_account_number } = employee || {};

  useEffect(() => {
    if (employeeId) {
      setForm({
        bank_name: bank_name || "",
        bank_branch: bank_branch || "",
        bank_code: bank_code || "",
        bank_account_name: bank_account_name || "",
        bank_account_number: bank_account_number || "",
      });
    }
    // 照片上傳不應覆蓋尚未儲存的銀行欄位。
  }, [employeeId, bank_name, bank_branch, bank_code, bank_account_name, bank_account_number]);
  const handleChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!employee?.id) return;
    setSaving(true);

    const { error } = await supabase
      .from("employees")
      .update({
        bank_name: form.bank_name || null,
        bank_branch: form.bank_branch || null,
        bank_code: form.bank_code || null,
        bank_account_name: form.bank_account_name || null,
        bank_account_number: form.bank_account_number || null,
      })
      .eq("id", employee.id);

    if (error) {
      onToast("儲存失敗：" + error.message, "error");
    } else {
      onUpdate({ ...employee, ...form });
      onToast("薪資帳戶資料已儲存", "success");
      refreshProfileComplete();
    }
    setSaving(false);
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !employee?.id) {
      if (!employee?.id) onToast("無法上傳：未找到員工資料", "error");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      onToast("圖片過大，請選擇 5MB 以下的檔案", "error");
      return;
    }

    setUploading(true);
    try {
      const publicUrl = await uploadEmployeeFile(file, employee.id, "bank_card_image_url");
      onUpdate({ ...employee, bank_card_image_url: publicUrl });
      onToast("存摺封面已上傳", "success");
      refreshProfileComplete();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "上傳失敗";
      onToast(`上傳發生錯誤：${msg}`, "error");
    }
    setUploading(false);
  };

  const inputClass =
    "w-full px-3 py-2 rounded-lg border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 bg-background-50";

  return (
    <div className="bg-background-50 border border-background-200 rounded-xl p-5 md:p-6">
      <h2 className="text-base font-bold text-foreground-900 mb-1">薪資帳戶資料</h2>
      <p className="text-xs text-foreground-500 mb-4">儲存後將自動帶入「薪資申請」</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs text-foreground-600 mb-1.5">
            銀行名稱 <span className="text-accent-500">*</span>
          </label>
          <input
            type="text"
            value={form.bank_name}
            onChange={(e) => handleChange("bank_name", e.target.value)}
            className={inputClass}
            placeholder="例如：台灣銀行"
          />
        </div>
        <div>
          <label className="block text-xs text-foreground-600 mb-1.5">
            分行名稱 <span className="text-accent-500">*</span>
          </label>
          <input
            type="text"
            value={form.bank_branch}
            onChange={(e) => handleChange("bank_branch", e.target.value)}
            className={inputClass}
            placeholder="例如：台北分行"
          />
        </div>
        <div>
          <label className="block text-xs text-foreground-600 mb-1.5">
            銀行代碼 <span className="text-accent-500">*</span>
          </label>
          <input
            type="text"
            value={form.bank_code}
            onChange={(e) => handleChange("bank_code", e.target.value)}
            className={inputClass}
            placeholder="例如：004"
          />
        </div>
        <div>
          <label className="block text-xs text-foreground-600 mb-1.5">
            戶名 <span className="text-accent-500">*</span>
          </label>
          <input
            type="text"
            value={form.bank_account_name}
            onChange={(e) => handleChange("bank_account_name", e.target.value)}
            className={inputClass}
            placeholder="請輸入戶名"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs text-foreground-600 mb-1.5">
            帳號 <span className="text-accent-500">*</span>
          </label>
          <input
            type="text"
            value={form.bank_account_number}
            onChange={(e) => handleChange("bank_account_number", e.target.value)}
            className={`${inputClass} md:w-1/2`}
            placeholder="請輸入銀行帳號"
          />
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-background-100">
        <label className="block text-xs text-foreground-600 mb-2">
          存摺封面 / 提款卡正面照 <span className="text-accent-500">*</span>
        </label>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleUpload}
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-dashed border-background-300 hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-50"
        >
          {uploading ? (
            <i className="ri-loader-4-line animate-spin text-foreground-500" />
          ) : (
            <i className="ri-upload-cloud-2-line text-foreground-500" />
          )}
          <span className="text-sm text-foreground-500">
            {employee?.bank_card_image_url
              ? "已上傳"
              : uploading
                ? "處理中..."
                : "點此重新上傳"}
          </span>
        </button>
      </div>

      <div className="flex justify-end mt-5">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-5 py-2 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 whitespace-nowrap"
        >
          {saving ? "儲存中..." : "儲存"}
        </button>
      </div>
    </div>
  );
}